/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { createReducer, type Draft } from '@reduxjs/toolkit';

import * as ActionTypes from '../../actions';
import Constants, { publicConstants } from '../../../../constants';
import Utils from '../../../../utils/utils';
import { processErrorResponse } from '../../epics/util';
import { getMessagesPath, MESSAGES_PATH, getAllMessagesPaths } from '../../../../messages';
import { CONTAINER_NAMES } from '../../../../container/container-utils';
import { getParentContext, getParentContextName } from '../../../../utils/context-utils';
import type { Action } from '../../state-manager';
import type { PropertyModel } from '../../../../rules/types';
import { getPropertyMetadata } from '../../../../rules/rules-store';
import CaseUtils from '../../../../case/utils';
import { LocalizationService } from '../../../../locale/LocalizationService';
import environmentInfo from '../../../../environment-info';

import type { ErrorMessages, ErrorObj } from './types';

const VALIDATION_FAIL = 'Validation fail';
const INVALID_INPUTS = 'Invalid inputs';
const indexRegex = /\[(\d+)\]$/gi;
const numberRegex = /(\d+)/gi;
const indexPatternRegexInsideMessagekey = /\[.\]$/gi;

const clearMessageBeforeSubmit = (
  draft: Draft<Record<string, unknown>>,
  stateObj: any,
  context: string,
  initialPath = [Constants.CONTEXT_DATA, Constants.CASE_INFO.CASE_INFO, Constants.CONTENT]
) => {
  const validationMessages = Utils.getIn(stateObj, initialPath);

  if (validationMessages) {
    const allPaths: string[][] = [];
    const allMessagesPath = getAllMessagesPaths(validationMessages, [], allPaths);
    for (const path of allMessagesPath) {
      Utils.setIn(draft[context], [...initialPath, ...path], {});
    }
  }
};

const clearMessagesForContext = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  messagesPath: string[],
  action: Action
) => {
  const { property, pageReference, category, type, subType } = action.payload;
  const allMessages = Utils.getIn(draft[context], messagesPath);

  if (allMessages && category === Constants.CASEMESSAGES) {
    Utils.setIn(draft[context], getMessagesPath(property, pageReference, category), '');
  } else if (allMessages && allMessages.length > 0 && Array.isArray(allMessages)) {
    let filteredMessages;
    if (subType) {
      filteredMessages = allMessages.filter((msg) => !(msg.type === type && msg.subType === subType));
    } else {
      filteredMessages = allMessages.filter((msg) => msg.type !== type);
    }
    if (messagesPath && messagesPath.length > 0) {
      Utils.setIn(draft[context], getMessagesPath(property, pageReference, category), filteredMessages);
    }
  }
};

const clearMessages = (draft: Draft<Record<string, unknown>>, action: Action) => {
  const { property, pageReference, category, isListEntry, stateObj } = action.payload;
  const { context } = action.payload;
  let parentcontext = context;

  if (stateObj) {
    clearMessageBeforeSubmit(draft, stateObj, context);
    return draft;
  }
  const messagesPath = getMessagesPath(property, pageReference, category, isListEntry);
  if (category === Constants.PAGE && context?.includes(CONTAINER_NAMES.WORKAREA)) {
    parentcontext = getParentContextName(context);
  }
  // Clearing the error messages in parent context.
  clearMessagesForContext(draft, parentcontext, messagesPath, action);
  // Clearing the error messages in context present in payload, This helps to clear the messages in workarea context.
  clearMessagesForContext(draft, context, messagesPath, action);
  return draft;
};

const sortMessageKeys = (keys: string[]): string[] => {
  return [...keys].sort((a, b) => {
    const aIndexed = a.match(indexRegex);
    const bIndexed = b.match(indexRegex);
    if (!aIndexed && !bIndexed) {
      return a.localeCompare(b);
    }
    if (!aIndexed && bIndexed) {
      return -1;
    }
    if (aIndexed?.length && bIndexed?.length) {
      const aIndex = aIndexed[0].match(numberRegex);
      const bIndex = bIndexed[0].match(numberRegex);
      if (aIndex?.length && bIndex?.length) {
        return parseInt(aIndex[0], 10) - parseInt(bIndex[0], 10);
      }
    }
    return 1;
  });
};

const clearMessagesFromList = (draft: Draft<Record<string, unknown>>, action: Action) => {
  const { property, pageReference, category, isListEntry, deletingIndex, stateObj } = action.payload;
  const { context } = action.payload;

  if (stateObj) {
    clearMessageBeforeSubmit(draft, stateObj, context);
    return draft;
  }

  const messagesPath = getMessagesPath(property, pageReference, category, isListEntry);

  if (Number.isInteger(deletingIndex)) {
    Utils.deleteIn(draft[context], messagesPath);
    const propertyTrimmedPath = messagesPath.slice(0, messagesPath.length - 1);
    let allMessageListItems = Utils.getIn(draft[context], propertyTrimmedPath) || {};
    const sortedMessageKeys = sortMessageKeys(Object.keys(allMessageListItems));
    allMessageListItems = sortedMessageKeys.map((item) => allMessageListItems[item]);
    allMessageListItems.forEach((item: any, index: number) => {
      const regexMatch = sortedMessageKeys[index].match(indexRegex);
      if (regexMatch?.length) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const idxFromMatch = parseInt(regexMatch[0].match(numberRegex)![0], 10);
        if (idxFromMatch > deletingIndex) {
          Utils.deleteIn(draft[context], propertyTrimmedPath.concat(sortedMessageKeys[index]));
          Utils.setIn(
            draft[context],
            propertyTrimmedPath.concat(
              sortedMessageKeys[index].replace(indexPatternRegexInsideMessagekey, `[${idxFromMatch - 1}]`)
            ),
            item
          );
        }
      }
    });
  } else {
    Utils.setIn(draft[context], messagesPath, {});
  }

  return draft;
};

/**
 * return the page reference for error field
 * @param errorField this field can be a field OR embedded data property (field OR EmbeddedData.field1)
 */
function getPageReferenceForErrorField(errorField: string): string | null {
  let pageReference: string | null = null;
  const lastDotPosition = errorField.lastIndexOf('.');
  if (lastDotPosition < 0) {
    return null;
  }
  pageReference = errorField.substring(0, lastDotPosition);
  return pageReference ? `caseInfo.content.${pageReference}`.replace(/\(/g, '[').replace(/\)/g, ']') : pageReference;
}

function checkIfDataObject(draft: Draft<Record<string, unknown>>, context: string) {
  const storeDataObj = Utils.getIn(draft, context);
  return storeDataObj?.dataInfo?.content !== undefined;
}

const checkIfObject = (draft: Draft<Record<string, unknown>>, context: string) => {
  const storeDataObj = Utils.getIn(draft, context);
  return CaseUtils.isObjectCaseType(storeDataObj?.objectInfo?.objectTypeID) === true;
};

function getFieldMessagesPath(draft: Draft<Record<string, unknown>>, context: string) {
  let fieldMessagesPath = MESSAGES_PATH.FIELD_MESSAGES_PATH;
  if (checkIfDataObject(draft, context)) {
    fieldMessagesPath = MESSAGES_PATH.DATA_OBJECT_FIELD_MESSAGES_PATH;
  } else if (checkIfObject(draft, context)) {
    fieldMessagesPath = MESSAGES_PATH.OBJECT_FIELD_MESSAGES_PATH;
  }
  return fieldMessagesPath;
}

const populateErrors = (draft: Draft<Record<string, unknown>>, context: string, err: ErrorMessages) => {
  const fieldInPageWithoutDot = Utils.getIn(err, Constants.ERROR_FIELD_IN_PAGE).slice(1);
  const fieldIdentifier = Utils.getIn(err, Constants.ERROR_IN_PAGE_IDENTIFIER).slice(1);

  if (fieldIdentifier) {
    let parentPageReference = 'caseInfo.content';
    if (checkIfDataObject(draft, context)) {
      parentPageReference = 'dataInfo.content';
    } else if (checkIfObject(draft, context)) {
      parentPageReference = 'objectInfo.content';
    }
    let pageReference = fieldInPageWithoutDot ? `${parentPageReference}.${fieldInPageWithoutDot}` : parentPageReference;
    pageReference = pageReference.replace(/\(/g, '[').replace(/\)/g, ']');
    const messagePath = getMessagesPath(fieldIdentifier, pageReference, Constants.PROPERTY);
    const classID = Utils.getIn(draft[context], `${pageReference}.classID`);
    const fieldMeta = getPropertyMetadata(fieldIdentifier, classID) as PropertyModel;
    const locale = environmentInfo.getLocale() ?? 'en-US';
    const localizationService: LocalizationService = new LocalizationService(locale, fieldMeta?.localeReference);

    const newError = {
      type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
      message: err.localizedValue,
      ...(fieldMeta?.label && {
        fieldLabel: localizationService.getLocalizedText(fieldMeta.label)
      })
    };

    const existingErrors = Utils.getIn(draft[context], messagePath, []);
    if (Utils.isContains(existingErrors, newError)) return;
    Utils.setIn(draft[context], messagePath, [...existingErrors, newError]);
  }
};

/**
 * @param errors - typeof mockErrorResponse from "mock-state.js" file under tests of field level errors
 * @returns typeof { field1: { messages: { error: ["error message-1 about the field 1", "error message-2 about the field 1"] } } }
 */

export const addFieldErrors = (draft: Draft<Record<string, unknown>>, context: string, errors: ErrorMessages[]) => {
  errors.forEach((err) => {
    populateErrors(draft, context, err);
  });
};

/**
 * @param pageErrors - typeof mockErrorResponse from "mock-state.js" file under tests of page level errors
 * @returns typeof { messages: [ { type: "info" | "error", message: "error message about the field" } ] }
 */

export const buildPageErrors = (pageErrors: ErrorMessages[]) => {
  const errors: ErrorObj[] = [];

  pageErrors.forEach((err) => {
    errors.push({
      type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
      message: err.localizedValue
    });
  });

  return errors;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function publishValidationErrors(draft: Draft<Record<string, unknown>>, action: { type: string; payload: any }) {
  const { err, formFields } = action.payload;
  let { context } = action.payload;
  const { fieldErrors, pageErrors } = processErrorResponse(err.errorDetails, formFields);
  const fieldMessagesPath = getFieldMessagesPath(draft, context);

  Utils.setIn(draft, [context, ...fieldMessagesPath], {});
  Utils.setIn(draft, [context, ...MESSAGES_PATH.PAGE_MESSAGES_PATH], []);

  // BUG-748769: Setting an empty array for all the embedded data properties similar to
  // MESSAGES_PATH.PAGE_MESSAGES_PATH OR MESSAGES_PATH.FIELD_MESSAGES_PATH
  fieldErrors.forEach((error: ErrorMessages) => {
    const errorField = Utils.getIn(error, Constants.ERROR_FIELD_IN_PAGE);
    if (errorField) {
      const fieldInPageWithoutDot = errorField.slice(1);
      if (fieldInPageWithoutDot) {
        const pageReference = getPageReferenceForErrorField(fieldInPageWithoutDot);
        if (!pageReference) {
          return false;
        }
        const property = fieldInPageWithoutDot.substring(fieldInPageWithoutDot.lastIndexOf('.') + 1);
        const messagePath = getMessagesPath(property, pageReference, Constants.PROPERTY);
        Utils.setIn(draft, [context, ...messagePath], []);
      }
    }
  });

  if (fieldErrors.length > 0) {
    addFieldErrors(draft, context, fieldErrors);
  }

  if (pageErrors.length > 0) {
    const content = buildPageErrors(pageErrors);

    if (!draft[context]) {
      // store page messaged in app when there is no context is present in store
      context = Constants.APP.APP;
    }

    Utils.setIn(draft, [context, ...MESSAGES_PATH.PAGE_MESSAGES_PATH], content);
  } else {
    if (context.includes && context.includes(CONTAINER_NAMES.WORKAREA)) {
      context = getParentContextName(context);
    }
    Utils.deleteIn(draft, [context, ...MESSAGES_PATH.PAGE_MESSAGES_PATH]);
  }

  return draft;
}

const errorReducer = createReducer({}, (builder) => {
  builder
    .addCase(ActionTypes.ERROR, (state, action: any) => {
      if (
        action.payload.err.errorDetails &&
        (action.payload.err.errorClassification === VALIDATION_FAIL ||
          action.payload.err.errorClassification === INVALID_INPUTS ||
          (action.payload.err?.displayButtonsOnErrors?.length ?? 0) > 0)
      ) {
        // errorDetails populates with validation errors
        return publishValidationErrors(state, action);
      }
      return state;
    })
    .addCase(ActionTypes.UPDATE_CONTAINER_ITEM, (state, action: any) => {
      const { context } = action.payload;
      const fieldMessagesPath = [context, ...getFieldMessagesPath(state, context)];
      const pageMessagesPath = [context, ...MESSAGES_PATH.PAGE_MESSAGES_PATH];

      Utils.setIn(state, fieldMessagesPath, {});
      Utils.setIn(state, pageMessagesPath, []);

      if (context && context.includes(CONTAINER_NAMES.WORKAREA)) {
        const parentContext = getParentContext(context);
        const parentPageMessagesPath = [parentContext, ...MESSAGES_PATH.PAGE_MESSAGES_PATH];
        Utils.setIn(state, parentPageMessagesPath, []);
      }
    })
    .addCase(ActionTypes.CLEAR_MESSAGES, (state: any, action: any) => {
      const { isListEntry, clearAll, context } = action.payload;
      if (clearAll) {
        Utils.deleteIn(state, [context, Constants.CONTEXT_DATA]);
        return state;
      }
      if (isListEntry) {
        return clearMessagesFromList(state, action);
      }
      return clearMessages(state, action);
    })
    .addCase(ActionTypes.ADD_MESSAGES, (state: any, action: any) => {
      const { messages, property, pageReference, context, category } = action.payload;
      const messagesPath = getMessagesPath(property, pageReference, category);

      if (messages && messages.length > 0 && Array.isArray(messages) && messagesPath && messagesPath.length > 0) {
        Utils.setIn(state[context], getMessagesPath(property, pageReference, category), messages);
      }
    });
});

export default errorReducer;
